import React from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { AiOutlineClose } from "react-icons/ai";
import { IoIosCloseCircle } from "react-icons/io";
import SummaryApi from "../common";
import { DatePicker } from "antd";
import dayjs from "dayjs";

// ────────────────────────────────────────────────
// THEME — same rules as the other files
// primary → modal title, form labels, primary button
// ────────────────────────────────────────────────
const THEME = {
  primary: "rgb(23, 44, 87)", // #172C57
  primaryLight: "rgba(23, 44, 87, 0.08)",
  actionBlue: "#2563EB",
  actionBlueHover: "#1D4ED8",
};

const districts = [
  "Alluri Sitharama Raju",
  "Anakapalli",
  "Ananthapuram",
  "Annamayya",
  "Bapatla",
  "CHITTOOR",
  "EAST GODAVARI",
  "Eluru",
  "Guntur",
  "Kakinada",
  "Konaseema",
  "Krishna",
  "Kurnool",
  "Manyam",
  "Nandyal",
  "NTR",
  "Palnadu",
  "Prakasam",
  "Srikakulam",
  "SPS Nellore",
  "Sri Satyasai",
  "Tirupathi",
  "Vizianagaram",
  "Visakhapatnam",
  "West Godavari",
  "YSR Kadapa",
];


const parseFollowUp = (value) => {
  if (value === null || value === undefined) return null;

  if (dayjs.isDayjs(value)) {
    return value.isValid() && value.year() > 0 ? value : null;
  }

  // Strip zero-width / BOM chars and trim — API responses sometimes carry them.
  const str = String(value).replace(/[\u200B-\u200D\uFEFF]/g, "").trim();
  if (!str) return null;

  // Any MySQL zero-date: "0000-00-00", "0000-00-00 00:00:00",
  // "0000-00-00 00:00:00.000000" — treat as empty.
  if (/^0{4}[-/]0{2}[-/]0{2}/.test(str)) return null;

  const strict = dayjs(str, "YYYY-MM-DD HH:mm:ss", true);
  if (strict.isValid() && strict.year() > 0) return strict;

  const loose = dayjs(str);
  return loose.isValid() && loose.year() > 0 ? loose : null;
};

const StudentFormModal = ({
  modalIsOpen,
  closeModal,
  isUpdate,
  isUpload,
  formData,
  setFormData,
  handleChange,
  handleSubmit,
  loadingSubmit,
  selectedCourses,
  setSelectedCourses,
  handleSelectChange,
  handleRemoveTag,
  memberType,
  selectedStudent,
}) => {

  const ugCourses = [
    "CSE",
    "CSE(AI & ML)",
    "CSE(AI & DS)",
    "AERONAUTICAL ENGINERRING",
    "ECE",
  ];


  const pgCourses = [];
  const courses =
    formData.program_type === "UG"
      ? ugCourses
      : formData.program_type === "PG"
        ? pgCourses
        : [];

  const isEditable = isUpdate || isUpload;




  // ── Disable past dates & times ──────────────────────────────────────
  // Disable any calendar day strictly before today.
  const disabledDate = (current) => {
    if (!current) return false;
    return current.endOf("day").isBefore(dayjs().startOf("day"));
  };

  // Disable past hours/minutes/seconds ONLY when the chosen day is today.
  const disabledTime = (current) => {
    if (!current || !current.isSame(dayjs(), "day")) {
      return {
        disabledHours: () => [],
        disabledMinutes: () => [],
        disabledSeconds: () => [],
      };
    }
    const now = dayjs();
    return {
      disabledHours: () => {
        const hours = [];
        for (let i = 0; i < now.hour(); i++) hours.push(i);
        return hours;
      },
      disabledMinutes: (selectedHour) => {
        if (selectedHour !== now.hour()) return [];
        const mins = [];
        for (let i = 0; i <= now.minute(); i++) mins.push(i);
        return mins;
      },
      disabledSeconds: (selectedHour, selectedMinute) => {
        if (selectedHour !== now.hour() || selectedMinute !== now.minute()) {
          return [];
        }
        const secs = [];
        for (let i = 0; i <= now.second(); i++) secs.push(i);
        return secs;
      },
    };
  };

  return (
    <Modal
      isOpen={modalIsOpen}
      onRequestClose={closeModal}
      contentLabel="Student Details"
      className="mx-auto my-10 mt-[100px] max-h-[85vh] max-w-full sm:max-w-4xl bg-white rounded-lg shadow-lg overflow-auto outline-none"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-50"
      ariaHideApp={false}
    >
      {/* Sticky header with title + close button */}
      <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-slate-100">
        <div className="flex justify-between items-center">
          <h2
            className="text-2xl font-semibold"
            style={{ color: THEME.primary }}
          >
            {isUpdate ? "Edit Student" : isUpload ? "Add Student" : "View Details"}
          </h2>
          <button
            onClick={closeModal}
            className="text-gray-500 hover:text-gray-800 transition-colors"
          >
            <AiOutlineClose size={22} />
          </button>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-6"
      >
        <div className="flex flex-col">
          <label
            htmlFor="student_name"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            Student Name<span className="ml-1 text-red-600">*</span>
          </label>
          <input
            type="text"
            id="student_name"
            name="student_name"
            value={formData.student_name}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
            required
          />
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="gender"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            Gender
          </label>
          <select
            id="gender"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="father_name"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            Father Name
          </label>
          <input
            type="text"
            id="father_name"
            name="father_name"
            value={formData.father_name}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
          />
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="mobile_number1"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            Mobile Number 1<span className="ml-1 text-red-600">*</span>
          </label>
          <input
            type="tel"
            id="mobile_number1"
            name="mobile_number1"
            value={formData.mobile_number1}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value) && value.length <= 10) {
                setFormData((prev) => ({
                  ...prev,
                  mobile_number1: value,
                }));
              }
            }}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={true}
            required
            pattern="\d{10}"
            title="Mobile number must be exactly 10 digits."
          />
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="mobile_number2"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            Mobile Number 2:
          </label>
          <input
            type="tel"
            id="mobile_number2"
            name="mobile_number2"
            value={formData.mobile_number2}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value) && value.length <= 10) {
                setFormData((prev) => ({
                  ...prev,
                  mobile_number2: value,
                }));
              }
            }}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
            pattern="\d{10}"
            title="Mobile number must be exactly 10 digits."
          />
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="email"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            Email:
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
          />
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="aadhar_number"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            Aadhar Number:
          </label>
          <input
            type="text"
            id="aadhar_number"
            name="aadhar_number"
            value={formData.aadhar_number}
            onChange={(e) => {
              const rawValue = e.target.value.replace(/-/g, "");
              const formattedValue = rawValue
                .replace(/\D/g, "")
                .replace(/(\d{4})(?=\d)/g, "$1-");

              if (formattedValue.length <= 14) {
                setFormData((prev) => ({
                  ...prev,
                  aadhar_number: formattedValue,
                }));
              }
            }}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
            pattern="(\d{4}-\d{4}-\d{4})"
            title="Aadhar number must be in the format 1234-5678-9012."
          />
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="state"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            State
          </label>
          <input
            type="text"
            id="state"
            name="state"
            value={formData.state}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
          />
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="district"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            District
          </label>
          <select
            id="district"
            name="district"
            value={formData.district}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
          >
            <option value="">Select District</option>
            {districts.map((district, index) => (
              <option key={index} value={district}>
                {district}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="city"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            City:
          </label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
          />
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="address"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            Address:
          </label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
          />
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="pincode"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            Pincode:
          </label>
          <input
            type="text"
            id="pincode"
            name="pincode"
            value={formData.pincode}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value) && value.length <= 6) {
                setFormData((prev) => ({
                  ...prev,
                  pincode: value,
                }));
              }
            }}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
            pattern="\d{6}"
            title="Pincode must be exactly 6 digits."
          />
        </div>

        {/* ────────── Optional Native District & Native Address ────────── */}
        <div className="flex flex-col">
          <label
            htmlFor="native_district"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            Native District:
          </label>
          <input
            type="text"
            id="native_district"
            name="native_district"
            value={formData.native_district || ""}
            onChange={handleChange}
            placeholder="Enter native district"
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
          />
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="native_address"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            Native Address:
          </label>
          <input
            type="text"
            id="native_address"
            name="native_address"
            value={formData.native_address || ""}
            onChange={handleChange}
            placeholder="Enter native address"
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
          />
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="course"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            Course
          </label>
          <input
            type="text"
            id="course"
            name="course"
            value={formData.course}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
          />
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="clg_name"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            College
          </label>
          <input
            type="text"
            id="clg_name"
            name="clg_name"
            value={formData.clg_name}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
          />
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="inter_percentage"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            Intermediate Percentage/CGPA
          </label>
          <input
            type="text"
            id="inter_percentage"
            name="inter_percentage"
            value={formData.inter_percentage}
            onChange={(e) => {
              const value = e.target.value;
              const regex = /^(\d+(\.\d*)?|\.\d+)?$/;
              if (regex.test(value) || value === "") {
                setFormData((prev) => ({
                  ...prev,
                  inter_percentage: value,
                }));
              }
            }}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
          />
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="program_type"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            Program Type
          </label>
          <select
            id="program_type"
            name="program_type"
            value={formData.program_type}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
          >
            <option value="">Select Program Type</option>
            <option value="UG">UG</option>
            <option value="PG">PG</option>
          </select>
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="ug_pg_course"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            UG/PG Course
          </label>
          <select
            id="ug_pg_course"
            name="ug_pg_course"
            value=""
            onChange={handleSelectChange}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
          >
            <option value="">Select Course</option>
            {courses.map((course) => (
              <option key={course} value={course}>
                {course}
              </option>
            ))}
          </select>

          <div className="flex flex-wrap gap-2 mt-2">
            {selectedCourses.map((course) => (
              <div
                key={course}
                className="flex items-center rounded-full px-3 py-1 border"
                style={{
                  backgroundColor: THEME.primaryLight,
                  borderColor: "rgba(23, 44, 87, 0.2)",
                  color: THEME.primary,
                }}
              >
                <span>{course}</span>
                <button
                  type="button"
                  className="ml-2"
                  disabled={!isEditable}
                  onClick={() => handleRemoveTag(course)}
                >
                  <IoIosCloseCircle
                    className="w-5 h-5"
                    style={{ color: THEME.primary }}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="call_duration"
            className="flex items-center text-lg font-semibold mb-2"
            style={{ color: THEME.primary }}
          >
            Call Duration
          </label>
          <div className="flex flex-row space-x-2">
            <div className="relative">
              <input
                type="text"
                id="hours"
                name="hours"
                value={formData.hours.padStart(2, "0")}
                className="h-10 bg-gray-100 border border-gray-300 text-gray-900 rounded-md w-16 text-center shadow-sm focus:outline-none cursor-not-allowed"
                disabled
              />
              <span className="absolute right-2 top-2 text-gray-500 font-bold">
                h
              </span>
            </div>
            <div className="flex items-center text-gray-500 font-bold">:</div>
            <div className="relative">
              <input
                type="text"
                id="minutes"
                name="minutes"
                value={formData.minutes.padStart(2, "0")}
                className="h-10 bg-gray-100 border border-gray-300 text-gray-900 rounded-md w-16 text-center shadow-sm focus:outline-none cursor-not-allowed"
                disabled
              />
              <span className="absolute right-2 top-2 text-gray-500 font-bold">
                m
              </span>
            </div>
            <div className="flex items-center text-gray-500 font-bold">:</div>
            <div className="relative">
              <input
                type="text"
                id="seconds"
                name="seconds"
                value={formData.seconds.padStart(2, "0")}
                className="h-10 bg-gray-100 border border-gray-300 text-gray-900 rounded-md w-16 text-center shadow-sm focus:outline-none cursor-not-allowed"
                disabled
              />
              <span className="absolute right-2 top-2 text-gray-500 font-bold">
                s
              </span>
            </div>
          </div>
        </div>

        {/* ────────── Optional Next Follow-up Date & Time (Ant Design) ────────── */}
        <div className="flex flex-col col-span-1 sm:col-span-2">
          <label
            htmlFor="next_follow_up_date"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            Next Follow-up Date & Time (Optional):
          </label>
          <DatePicker
            id="next_follow_up_date"
            showTime={{
              use12Hours: true,
              format: "hh:mm A",
              hideDisabledOptions: true,
            }}
            format="YYYY-MM-DD hh:mm A"
            needConfirm={false}
            placeholder="Select Follow-up Date & Time"
            className={`w-full p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
            disabledDate={disabledDate}
            disabledTime={disabledTime}
            defaultPickerValue={''}
            value={parseFollowUp(formData.next_follow_up_date) ?? undefined}
            onChange={(date) => {
              setFormData((prev) => ({
                ...prev,
                next_follow_up_date: date
                  ? date.format("YYYY-MM-DD HH:mm:ss")
                  : null,
              }));
            }}
          />
        </div>

        <div className="flex flex-col col-span-1 sm:col-span-2">
          <label
            htmlFor="call_status"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            Call Status<span className="ml-1 text-red-600">*</span>
          </label>
          <select
            id="call_status"
            name="call_status"
            value={formData.call_status}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!(isEditable || formData.call_status !== "Contacted") && "cursor-not-allowed"}`}
            disabled={formData.call_status === "Contacted" || !isEditable}
            required
          >
            <option value="">Select Status</option>
            <option value="Contacted">Contacted</option>
            <option value="Not Answered">Not Answered</option>
          </select>
        </div>

        <div className="flex flex-col col-span-1 sm:col-span-2">
          <label
            htmlFor="remarks"
            className="flex items-center text-sm font-semibold mb-1"
            style={{ color: THEME.primary }}
          >
            Remarks:
          </label>
          <textarea
            id="remarks"
            name="remarks"
            value={formData.remarks}
            onChange={handleChange}
            className={`p-2 bg-slate-100 border rounded ${!isEditable && "cursor-not-allowed"}`}
            disabled={!isEditable}
            rows={2}
          />
        </div>

        <div className="flex flex-col col-span-1 sm:col-span-2">
          <div className="flex flex-wrap gap-y-2">
            <label
              className="flex items-center mr-4 text-sm font-semibold"
              style={{ color: THEME.primary }}
            >
              <input
                type="radio"
                name="is_interested"
                value="Not Interested"
                checked={formData.is_interested === "Not Interested"}
                onChange={handleChange}
                className="mr-2"
                disabled={
                  formData.call_status === "Not Answered" ||
                  formData.call_status === "" ||
                  !isEditable
                }
              />
              Not Interested
            </label>
            <label
              className="flex items-center mr-4 text-sm font-semibold"
              style={{ color: THEME.primary }}
            >
              <input
                type="radio"
                name="is_interested"
                value="Interested"
                checked={formData.is_interested === "Interested"}
                onChange={handleChange}
                className="mr-2"
                disabled={
                  formData.call_status === "Not Answered" ||
                  formData.call_status === "" ||
                  !isEditable
                }
              />
              Interested
            </label>
            <label
              className="flex items-center mr-4 text-sm font-semibold"
              style={{ color: THEME.primary }}
            >
              <input
                type="radio"
                name="is_interested"
                value="Most Interested"
                checked={formData.is_interested === "Most Interested"}
                onChange={handleChange}
                className="mr-2"
                disabled={
                  formData.call_status === "Not Answered" ||
                  formData.call_status === "" ||
                  !isEditable
                }
              />
              Most Interested
            </label>

            <label
              className="flex items-center mr-4 text-sm font-semibold"
              style={{ color: THEME.primary }}
            >
              <input
                type="radio"
                name="is_interested"
                value="Call Back"
                checked={formData.is_interested === "Call Back"}
                onChange={handleChange}
                className="mr-2"
                disabled={
                  formData.call_status === "Not Answered" ||
                  formData.call_status === "" ||
                  !isEditable
                }
              />
              Call Back
            </label>

            <label
              className="flex items-center mr-4 text-sm font-semibold"
              style={{ color: THEME.primary }}
            >
              <input
                type="radio"
                name="is_interested"
                value="Others"
                checked={formData.is_interested === "Others"}
                onChange={handleChange}
                className="mr-2"
                disabled={
                  formData.call_status === "Not Answered" ||
                  formData.call_status === "" ||
                  !isEditable
                }
              />
              Others
            </label>
          </div>

          <div className="flex items-center mt-4">
            {formData.is_interested === "Call Back" && (
              <div className="mt-2 flex items-center justify-center">
                <label
                  htmlFor="call_back_reason"
                  className="block mb-2 text-sm font-semibold"
                  style={{ color: THEME.primary }}
                >
                  Call Back Reason<span className="ml-1 text-red-600">*</span>:
                </label>
                <select
                  id="call_back_reason"
                  name="call_back_reason"
                  value={formData.call_back_reason || ""}
                  onChange={handleChange}
                  className="p-2 border ml-4 border-gray-300 rounded"
                  disabled={!isEditable}
                  required={formData.is_interested === "Call Back"}
                >
                  <option value="">Select Reason</option>
                  <option value="IPE Exams">IPE Exams</option>
                  <option value="Jee Mains Results">JEE Mains Results</option>
                  <option value="Jee Mains-2 Results">
                    JEE Mains-2 Results
                  </option>
                  <option value="EAMCET RESULTS">EAMCET RESULTS</option>
                  <option value="IPE Results">IPE Results</option>
                  <option value="Supply Exam">Supply Exam</option>
                  <option value="Supply Results">Supply Results</option>
                  <option value="EOD">EOD</option>
                </select>
              </div>
            )}

            {formData.is_interested === "Others" && (
              <div className="mt-2 flex items-center justify-center">
                <label
                  htmlFor="other_reasons"
                  className="block mb-2 text-sm font-semibold"
                  style={{ color: THEME.primary }}
                >
                  Other Reason<span className="ml-1 text-red-600">*</span>:
                </label>
                <select
                  id="other_reasons"
                  name="other_reasons"
                  value={formData.other_reasons || ""}
                  onChange={handleChange}
                  className="p-2 border ml-4 border-gray-300 rounded"
                  disabled={!isEditable}
                  required={formData.is_interested === "Others"}
                >
                  <option value="">Select Reason</option>
                  <option value="Wrong Number">Wrong Number</option>
                  <option value="Not Reachable">Not Reachable</option>
                  <option value="Number Not Working">
                    Number Not Working
                  </option>
                  <option value="Call Declined">Call Declined</option>
                  <option value="Busy">Busy</option>
                  <option value="Switched Off">Switched Off</option>
                  <option value="Incoming Calls Stopped">
                    Incoming Calls Stopped
                  </option>
                </select>
              </div>
            )}
          </div>
        </div>

        {isEditable && (
          <div className="flex flex-col col-span-1 sm:col-span-2">
            <button
              type="submit"
              style={{ backgroundColor: THEME.primary }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              className={`text-white px-4 py-2 rounded shadow-sm transition-opacity ${loadingSubmit && "cursor-not-allowed opacity-60"}`}
              disabled={loadingSubmit}
            >
              {loadingSubmit
                ? "Submitting..."
                : isUpdate
                  ? "Update Student"
                  : "Add Student"}
            </button>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default StudentFormModal;